import type {
	BadgeIndicatorColorScheme,
	BadgeIndicatorCssVars,
	BadgeIndicatorSize,
	BadgeIndicatorThemeConfig,
	BadgeIndicatorThemeName,
	BadgeIndicatorVariant
} from './types';

const createBadgeIndicatorColorScheme = ({
	border,
	foreground,
	softBackground,
	solidBackground,
	solidForeground
}: BadgeIndicatorThemeConfig): Record<
	BadgeIndicatorVariant,
	BadgeIndicatorColorScheme
> => ({
	soft: {
		background: softBackground,
		foreground,
		border,
		accent: solidBackground,
		gradientA: softBackground,
		gradientB: `color-mix(in srgb, white 72%, ${softBackground})`,
		gradientC: softBackground,
		shimmer: 'color-mix(in srgb, white 80%, transparent)'
	},
	solid: {
		background: solidBackground,
		foreground: solidForeground,
		border,
		accent: solidBackground,
		gradientA: solidBackground,
		gradientB: `color-mix(in srgb, ${solidBackground} 55%, white)`,
		gradientC: solidBackground,
		shimmer: 'color-mix(in srgb, white 60%, transparent)'
	},
	outline: {
		background: 'transparent',
		foreground,
		border,
		accent: solidBackground,
		gradientA: 'transparent',
		gradientB: border,
		gradientC: 'transparent',
		shimmer: `color-mix(in srgb, ${border} 85%, transparent)`
	}
});

export const BADGE_INDICATOR_COLOR_SCHEMES = {
	grey: createBadgeIndicatorColorScheme({
		softBackground: '#f3f4f6',
		foreground: '#6a7282',
		border: '#e5e7eb',
		solidBackground: '#4a5565',
		solidForeground: '#f9fafb'
	}),
	red: createBadgeIndicatorColorScheme({
		softBackground: '#ffe2e2',
		foreground: '#e7000b',
		border: '#ffc9c9',
		solidBackground: '#e7000b',
		solidForeground: '#fef2f2'
	}),
	green: createBadgeIndicatorColorScheme({
		softBackground: '#dcfce7',
		foreground: '#00a63e',
		border: '#b9f8cf',
		solidBackground: '#00a63e',
		solidForeground: '#f0fdf4'
	}),
	yellow: createBadgeIndicatorColorScheme({
		softBackground: '#fef9c2',
		foreground: '#a65f00',
		border: '#fee685',
		solidBackground: '#f0b100',
		solidForeground: '#fefce8'
	}),
	blue: createBadgeIndicatorColorScheme({
		softBackground: '#dbeafe',
		foreground: '#1447e6',
		border: '#bedbff',
		solidBackground: '#155dfc',
		solidForeground: '#eff6ff'
	}),
	pink: createBadgeIndicatorColorScheme({
		softBackground: '#f3e8ff',
		foreground: '#8200db',
		border: '#e9d4ff',
		solidBackground: '#9810fa',
		solidForeground: '#faf5ff'
	}),
	purple: createBadgeIndicatorColorScheme({
		softBackground: '#e0e7ff',
		foreground: '#432dd7',
		border: '#c6d2ff',
		solidBackground: '#4f39f6',
		solidForeground: '#eef2ff'
	})
} satisfies Record<
	BadgeIndicatorThemeName,
	Record<BadgeIndicatorVariant, BadgeIndicatorColorScheme>
>;

export const BADGE_INDICATOR_SIZE_STYLES = {
	default: 'h-6 px-2 text-xs leading-4',
	small: 'h-5 px-1.5 text-[10px] leading-[14px]'
} satisfies Record<BadgeIndicatorSize, string>;

export const BADGE_INDICATOR_ANIMATION_TOKENS = {
	duration: '1560ms',
	delay: '0ms',
	easing: 'cubic-bezier(.4,0,.2,1)',
	iteration: 'infinite',
	intensity: '0.5',
	playState: 'running'
} as const;

export const getBadgeIndicatorCssVars = (
	theme: BadgeIndicatorThemeName,
	variant: BadgeIndicatorVariant
): BadgeIndicatorCssVars => {
	const colorScheme = BADGE_INDICATOR_COLOR_SCHEMES[theme][variant];

	return {
		'--ab-bg': colorScheme.background,
		'--ab-fg': colorScheme.foreground,
		'--ab-border': colorScheme.border,
		'--ab-accent': colorScheme.accent,
		'--ab-grad-a': colorScheme.gradientA,
		'--ab-grad-b': colorScheme.gradientB,
		'--ab-grad-c': colorScheme.gradientC,
		'--ab-shimmer': colorScheme.shimmer,
		'--ab-dur': BADGE_INDICATOR_ANIMATION_TOKENS.duration,
		'--ab-delay': BADGE_INDICATOR_ANIMATION_TOKENS.delay,
		'--ab-easing': BADGE_INDICATOR_ANIMATION_TOKENS.easing,
		'--ab-iter': BADGE_INDICATOR_ANIMATION_TOKENS.iteration,
		'--ab-intensity': BADGE_INDICATOR_ANIMATION_TOKENS.intensity,
		'--ab-play': BADGE_INDICATOR_ANIMATION_TOKENS.playState
	};
};
